/* gbm-bake-ship-calendar.js
   Master calendar for Bakery Box shipping dates.

   Standard:
   - Cutoff:  Wednesday 9:00 AM ET
   - Ship:    Thursday
   - Arrival: Friday (overnight)

   Holiday exceptions 2025:
   - Thanksgiving:
       Cutoff:  Sunday 2025-11-23 @ 11:59 PM ET
       Ship:    Tuesday 2025-11-25
       Arrival: Wednesday 2025-11-26
       No ship: Thursday 2025-11-27

   - Christmas / New Year:
       Cutoff:  Sunday 2025-12-21 @ 11:59 PM ET
       Ship:    Monday 2025-12-22
       Arrival: Tuesday 2025-12-23
       Closed:  No shipments between 2025-12-23 and 2026-01-08
*/

(function () {
  const CONFIG = {
    timeZone: "America/New_York",

    // Standard recurring ship weekday(s): Thursday only
    // 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
    shipWeekdays: [4],

    // Standard cutoff: "daysBefore" at "hour:minute" ET (24h)
    // For Thursday shipping, cutoff is Wednesday 09:00
    cutoff: { daysBefore: 1, hour: 9, minute: 0 },

    // Blackout dates (no shipping) in YYYY-MM-DD
    // - Thanksgiving Day
    // - Christmas Day
    // - New Year's Day (during closure)
    blackoutDates: [
      "2025-11-27",
      "2025-12-25",
      "2026-01-01",
    ],

    // Special runs with their own cutoff rules
    // Key = ship date; cutoff.date = cutoff calendar day at given time (ET)
    specialRuns: {
      // Thanksgiving week
      "2025-11-25": {
        // Cutoff: Sunday 11/23/25 @ 11:59 PM ET
        cutoff: { date: "2025-11-23", hour: 23, minute: 59 },
      },
      // Christmas week
      "2025-12-22": {
        // Cutoff: Sunday 12/21/25 @ 11:59 PM ET
        cutoff: { date: "2025-12-21", hour: 23, minute: 59 },
      },
    },

    // Global manual override for next ship date (highest precedence)
    // Leave "" to auto-calc from rules
    manualOverrideDate: "",
  };

  // ---------- Utility helpers ----------
  function partsFrom(date, tz) {
    const f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    });
    const p = f.formatToParts(date).reduce((o, part) => {
      if (part.type !== "literal") o[part.type] = parseInt(part.value, 10);
      return o;
    }, {});
    const wd = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      weekday: "short",
    }).format(date);
    const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    p.weekday = map[wd] ?? date.getDay();
    return p; // {year, month, day, hour, minute, weekday}
  }

  function serialET(y, m, d, h, mi) {
    return y * 1e8 + m * 1e6 + d * 1e4 + h * 1e2 + mi;
  }

  function toYYYYMMDD(y, m, d) {
    return (
      String(y).padStart(4, "0") +
      "-" +
      String(m).padStart(2, "0") +
      "-" +
      String(d).padStart(2, "0")
    );
  }

  function addDays(date, days) {
    return new Date(date.getTime() + days * 86400000);
  }

  function isBlackout(key, cfg) {
    return (cfg.blackoutDates || []).includes(key);
  }

  function formatNice(y, m, d, tz) {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(new Date(Date.UTC(y, m - 1, d, 12, 0)));
  }

  // Build a Date that represents "y-m-d h:mi in ET" as a real instant in time.
  // Then the browser can display that instant in the user's local timezone.
  function buildInstantInET(y, m, d, hour, minute, tz) {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    });

    function getParts(date) {
      const parts = dtf.formatToParts(date);
      const out = {};
      for (const part of parts) {
        if (part.type !== "literal") {
          out[part.type] = parseInt(part.value, 10);
        }
      }
      return out;
    }

    const target = {
      year: y,
      month: m,
      day: d,
      hour: hour,
      minute: minute,
    };

    // Start near the "same numbers in UTC" and search ±6 hours in 5-minute steps.
    const base = Date.UTC(y, m - 1, d, hour, minute, 0);
    const maxOffsetMs = 6 * 60 * 60 * 1000;
    const stepMs = 5 * 60 * 1000;

    for (let delta = -maxOffsetMs; delta <= maxOffsetMs; delta += stepMs) {
      const candidate = new Date(base + delta);
      const p = getParts(candidate);
      if (
        p.year === target.year &&
        p.month === target.month &&
        p.day === target.day &&
        p.hour === target.hour &&
        p.minute === target.minute
      ) {
        return candidate;
      }
    }

    // Fallback: at least returns something close
    return new Date(base);
  }

  // ---------- Core: find next ship date ----------
  // ---------- Core: find next ship date ----------
  function computeNextShip(now, cfg) {
    const tz = cfg.timeZone;
    const nowP = partsFrom(now, tz);
    const nowSerial = serialET(
      nowP.year,
      nowP.month,
      nowP.day,
      nowP.hour,
      nowP.minute
    );

    // 1) Manual override (uses standard cutoff rule) – still highest precedence
    if (cfg.manualOverrideDate) {
      const [Y, M, D] = cfg.manualOverrideDate.split("-").map(Number);
      const key = toYYYYMMDD(Y, M, D);
      if (!isBlackout(key, cfg)) {
        const shipDate = new Date(Date.UTC(Y, M - 1, D, 12, 0));
        const cutoffBase = addDays(shipDate, -cfg.cutoff.daysBefore);
        const cb = partsFrom(cutoffBase, tz);
        const cutoffSerial = serialET(
          cb.year,
          cb.month,
          cb.day,
          cfg.cutoff.hour,
          cfg.cutoff.minute
        );
        if (nowSerial <= cutoffSerial) {
          return {
            y: Y,
            m: M,
            d: D,
            source: "override",
            cutoff: {
              y: cb.year,
              m: cb.month,
              d: cb.day,
              hour: cfg.cutoff.hour,
              minute: cfg.cutoff.minute,
            },
          };
        }
      }
    }

    // 2) Find next recurring Thursday shipment (within 60 days)
    let bestRecurring = null;
    let bestRecurringShipSerial = null;

    for (let i = 0; i < 60; i++) {
      const cand = addDays(now, i);
      const p = partsFrom(cand, tz);
      const key = toYYYYMMDD(p.year, p.month, p.day);
      if (!cfg.shipWeekdays.includes(p.weekday)) continue;
      if (isBlackout(key, cfg)) continue;

      const cutoffBase = addDays(cand, -cfg.cutoff.daysBefore);
      const cb = partsFrom(cutoffBase, tz);
      const cutoffSerial = serialET(
        cb.year,
        cb.month,
        cb.day,
        cfg.cutoff.hour,
        cfg.cutoff.minute
      );

      if (nowSerial <= cutoffSerial) {
        bestRecurring = {
          y: p.year,
          m: p.month,
          d: p.day,
          source: "recurring",
          cutoff: {
            y: cb.year,
            m: cb.month,
            d: cb.day,
            hour: cfg.cutoff.hour,
            minute: cfg.cutoff.minute,
          },
        };
        bestRecurringShipSerial = serialET(p.year, p.month, p.day, 12, 0);
        break; // first valid recurring is the earliest by construction
      }
    }

    // 3) Find earliest valid special run (within its own cutoff)
    const specialRuns = cfg.specialRuns || {};
    const specialDates = Object.keys(specialRuns).sort(); // chronological

    let bestSpecial = null;
    let bestSpecialShipSerial = null;

    for (const shipKey of specialDates) {
      const [Y, M, D] = shipKey.split("-").map(Number);
      const key = toYYYYMMDD(Y, M, D);
      if (isBlackout(key, cfg)) continue;

      const run = specialRuns[shipKey] || {};
      let cutoffY, cutoffM, cutoffD, cutoffHour, cutoffMinute;

      if (run.cutoff && run.cutoff.date) {
        const [cY, cM, cD] = run.cutoff.date.split("-").map(Number);
        cutoffY = cY;
        cutoffM = cM;
        cutoffD = cD;
        cutoffHour =
          typeof run.cutoff.hour === "number"
            ? run.cutoff.hour
            : cfg.cutoff.hour;
        cutoffMinute =
          typeof run.cutoff.minute === "number"
            ? run.cutoff.minute
            : cfg.cutoff.minute;
      } else {
        // Fallback: apply standard daysBefore/time
        const specialDate = new Date(Date.UTC(Y, M - 1, D, 12, 0));
        const cutoffBase = addDays(specialDate, -cfg.cutoff.daysBefore);
        const cb = partsFrom(cutoffBase, tz);
        cutoffY = cb.year;
        cutoffM = cb.month;
        cutoffD = cb.day;
        cutoffHour = cfg.cutoff.hour;
        cutoffMinute = cfg.cutoff.minute;
      }

      const cutoffSerial = serialET(
        cutoffY,
        cutoffM,
        cutoffD,
        cutoffHour,
        cutoffMinute
      );
      if (nowSerial > cutoffSerial) continue; // too late for this special run

      const shipSerial = serialET(Y, M, D, 12, 0); // midday for date comparison
      if (
        !bestSpecial ||
        shipSerial < bestSpecialShipSerial
      ) {
        bestSpecial = {
          y: Y,
          m: M,
          d: D,
          source: "special",
          cutoff: {
            y: cutoffY,
            m: cutoffM,
            d: cutoffD,
            hour: cutoffHour,
            minute: cutoffMinute,
          },
        };
        bestSpecialShipSerial = shipSerial;
      }
    }

    // 4) Choose the earliest ship date between special and recurring
    if (bestSpecial && bestRecurring) {
      if (bestSpecialShipSerial < bestRecurringShipSerial) {
        return bestSpecial;
      }
      return bestRecurring;
    }
    if (bestSpecial) return bestSpecial;
    if (bestRecurring) return bestRecurring;

    // No shipment found in horizon
    return null;
  }

  function getNextShipInfo() {
    const cfg = window.GBM_SHIP_CAL_CONFIG
      ? { ...CONFIG, ...window.GBM_SHIP_CAL_CONFIG }
      : CONFIG;
    const now = new Date();
    const res = computeNextShip(now, cfg);
    if (!res) return null;

    const shipDate = new Date(Date.UTC(res.y, res.m - 1, res.d, 12, 0));
    const pretty = formatNice(res.y, res.m, res.d, cfg.timeZone);

    // Arrival = ship + 1 day (overnight)
    const arrivalDate = new Date(shipDate.getTime() + 86400000);
    const arrivalNice = new Intl.DateTimeFormat("en-US", {
      // Arrival is date-only; timezone difference inside the US won't change the day.
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(arrivalDate);

    // Format cutoff info
    const c = res.cutoff;
    // Build the *instant* that corresponds to that ET cutoff,
    // then display it in the customer's *local* time.
    const cutoffInstant = buildInstantInET(
      c.y,
      c.m,
      c.d,
      c.hour,
      c.minute,
      cfg.timeZone
    );

    const cutoffNice = new Intl.DateTimeFormat("en-US", {
      // NOTE: no timeZone here → user's local time zone
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(cutoffInstant);

    return {
      y: res.y,
      m: res.m,
      d: res.d,
      pretty,       // e.g., "Thu, Dec 4"
      source: res.source, // "override" | "special" | "recurring"
      cutoffNice,   // e.g., "Sun, Nov 23, 10:59 PM" (in shopper's local time)
      arrivalNice,  // e.g., "Fri, Nov 21"
      tz: cfg.timeZone,
    };
  }

  window.GBM_SHIP_CAL = {
    config: CONFIG,
    getNextShipInfo,
  };
})();
